import * as React from 'react';

import {
  IconRegularChevronLeft,
  IconRegularChevronRight,
  ModalContext,
  ModalModel,
} from '@notino/react-styleguide';
import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { ModalGallery } from '@components/ModalGallery';
import { PlayerWrapper } from '@components/ModalGallery/styled';
import { getVideoIframe } from '@containers/ProductDetailContainer/utils';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { isActivationKey } from '@helpers/accessibility/isActivationKey';
import { Arrow } from '@hooks/useArrows';
import { css } from '@linaria/core';

import { ImageSlider } from './ImageSlider';
import { MainImage } from './MainImg';
import { ModiFaceOverlay } from './ModiFaceOverlay';
import {
  Container,
  MainImgWrapper,
  ImageWrapper,
  VideoWrapper,
  StyledCol,
  PictogramWrapper,
  RightSideWrapper,
} from './styled';
import { ScrollThumbs } from './Thumbs/ScrollThumbs';
import { trackProductImageClick } from './tracking';
import { getImagesAttributes, getVideoAttrs } from './utils';

export const EAN_FOR_360_PHOTO = '4210201434658';

export interface IProductImageGalleryProps {
  media: NonNullable<
    GetCatalogProductViewQuery['productDetailByCatalogMasterId']
  >['variants'][number]['media'];
  domain: string;
  hasModiface?: boolean;
  openModifaceModal?: () => void;
  pictogram?: JSX.Element | null;
  rightSide?: JSX.Element | null;
}

export const ProductImageGallery: React.FC<IProductImageGalleryProps> = ({
  domain,
  media,
  openModifaceModal,
  hasModiface = false,
  pictogram,
  rightSide,
}) => {
  const [imageIndex, setImageIndex] = React.useState(0);
  const { toggleModal, hideModal } = ModalContext.useModalContext();

  const { getTimeFromInit } = useTrackingContext();

  const { imagesAttrsOrder, imagesAttrsZoom, imagesAttrsOriginal } =
    React.useMemo(() => {
      const imagesArray = [
        media?.mainImage,
        media?.previewImage,
        ...(media?.others ?? []),
      ].filter((item): item is NonNullable<typeof item> => !!item);

      const [mainImage, ...otherImages] = imagesArray;
      return {
        imagesAttrsOrder: [
          ...getImagesAttributes(imagesArray, domain, 'order'),
          ...(media?.videos ?? []).map((video) =>
            getVideoAttrs(video, 'mqdefault')
          ),
        ],
        imagesAttrsZoom: getImagesAttributes(imagesArray, domain, 'zoomImage'),
        imagesAttrsOriginal: [
          // let first image be in a little worse quality, so it loads faster for better LCP and UX
          ...getImagesAttributes([mainImage], domain, 'detailMain'),
          ...getImagesAttributes(otherImages, domain, 'detailGallery'),
        ],
      };
    }, [
      media?.mainImage,
      media?.previewImage,
      media?.others,
      media?.videos,
      domain,
    ]);

  const openMainImageModal = React.useCallback(
    (imgIndex?: number) => {
      trackProductImageClick({
        timing: getTimeFromInit(),
      });

      if (typeof imgIndex === 'number') {
        setImageIndex(imgIndex);
      }

      toggleModal(
        <ModalGallery
          onClose={hideModal}
          imageIndex={imgIndex ?? 0}
          images={imagesAttrsZoom}
          thumbImages={imagesAttrsOrder}
          videos={media?.videos ?? []}
        />,
        {
          center: true,
          size: ModalModel.ModalSizes.s1200,
          onlyTopBorder: true,
          noBorders: true,
          withFocusTrap: true,
          closeIconColor: 'icon.primary',
        }
      );
    },
    [
      getTimeFromInit,
      toggleModal,
      hideModal,
      imagesAttrsZoom,
      imagesAttrsOrder,
      media?.videos,
    ]
  );

  const handleMainImageClick = React.useCallback(
    (imgIndex?: number) => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      openMainImageModal(imgIndex);
    },
    [openMainImageModal]
  );

  const handleMainImageKeyDown = React.useCallback(
    (imgIndex?: number) => (e: React.KeyboardEvent<HTMLElement>) => {
      if (!isActivationKey(e)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      openMainImageModal(imgIndex);
    },
    [openMainImageModal]
  );

  const videoIndex = imageIndex - imagesAttrsOriginal.length;
  const isVideo: boolean = videoIndex >= 0;
  const isFirstItem = imageIndex === 0;
  const isLastItem =
    imageIndex ===
    imagesAttrsOriginal.length + (media?.videos?.length ?? 0) - 1;

  const shouldShowModiFaceOverlay = hasModiface && !isVideo && imageIndex === 0;

  return (
    <Container id="pdImageGallery">
      {pictogram ? <PictogramWrapper>{pictogram}</PictogramWrapper> : null}
      {rightSide ? <RightSideWrapper>{rightSide}</RightSideWrapper> : null}

      <ImageSlider
        imagesAttrs={imagesAttrsOriginal}
        drawerImagesAttrs={imagesAttrsZoom}
        drawerThumbImagesAttrs={imagesAttrsOrder}
        videos={media?.videos ?? []}
        hasModiface={hasModiface}
        openModifaceModal={openModifaceModal}
      />

      <StyledCol>
        <MainImgWrapper
          role="button"
          tabIndex={!isVideo ? 0 : -1}
          data-testid="main-image-wrapper"
          onClick={!isVideo ? handleMainImageClick(imageIndex) : undefined}
          onKeyDown={!isVideo ? handleMainImageKeyDown(imageIndex) : undefined}
        >
          {!isFirstItem && (
            <Arrow
              position="left"
              onClick={(e) => {
                e.stopPropagation();
                setImageIndex((i) => i - 1);
              }}
              role="button"
            >
              <IconRegularChevronLeft
                color="icon.primary"
                height="1.25rem"
                width="1.25rem"
              />
            </Arrow>
          )}
          <ImageWrapper>
            {!isVideo && (
              <MainImage
                imageAttrs={imagesAttrsOriginal[imageIndex]}
                onClick={handleMainImageClick(imageIndex)}
                index={imageIndex}
              />
            )}

            {isVideo && (
              <VideoWrapper>
                <PlayerWrapper
                  dangerouslySetInnerHTML={{
                    __html: media?.videos?.[videoIndex]
                      ? getVideoIframe(media.videos[videoIndex])
                      : '',
                  }}
                />
              </VideoWrapper>
            )}

            {shouldShowModiFaceOverlay && (
              <ModiFaceOverlay
                onClick={(e) => {
                  e.stopPropagation();
                  openModifaceModal?.();
                }}
              />
            )}
          </ImageWrapper>
          {!isLastItem && (
            <Arrow
              position="right"
              onClick={(e) => {
                e.stopPropagation();
                setImageIndex((i) => i + 1);
              }}
              role="button"
            >
              <IconRegularChevronRight
                color="icon.primary"
                height="1.25rem"
                width="1.25rem"
              />
            </Arrow>
          )}
        </MainImgWrapper>

        {imagesAttrsOrder.length > 1 && (
          <div
            className={css`
              margin-top: 1.25rem;
            `}
          >
            <ScrollThumbs
              imagesAttrs={imagesAttrsOrder}
              imageIndex={imageIndex}
              hideOnMobile={true}
              handleImageChange={setImageIndex}
              handleImageClick={handleMainImageClick}
              dataTestId="pd-gallery-slider"
            />
          </div>
        )}
      </StyledCol>
    </Container>
  );
};
